import type { TypedDocumentNode } from '@apollo/client';
import { gql, useMutation } from '@apollo/client';
import gqlMacro from 'graphql-tag.macro';
import type { ReactNode } from 'react';
import { useEffect, useMemo } from 'react';
import { useInterval, useTimeout } from 'react-use';

import type { FiatCurrency } from '#src/__generated__/globalTypes.ts';
import { LoadingIndicator } from '#src/atoms/loader/LoadingIndicator/index.tsx';
import { RedirectAfterSignIn } from '#src/contexts/currentUser/RedirectAfterSignIn.tsx';
import { currentUser } from '#src/contexts/currentUser/queries.ts';
import { coreCache } from '#src/contexts/graphql/Provider.tsx';
import { useGraphqlContext } from '#src/contexts/graphql/index.tsx';
import { useSportContext } from '#src/contexts/sport/index.ts';
import { useTMContext } from '#src/contexts/tm/index.ts';
import { useQuery } from '#src/hooks/graphql/useQuery.ts';
import { useQueryLoader } from '#src/hooks/graphql/useQueryLoader.ts';
import { useFeatureFlags } from '#src/hooks/useFeatureFlags.tsx';
import { usePageVisibility } from '#src/hooks/usePageVisibility.tsx';
import {
  getMinorRate,
  monetaryAmountFragment,
  ratesFragment,
} from '#src/lib/monetaryAmount.ts';
import { queryLoader } from '#src/lib/navigation/queryLoader.ts';
import {
  importantNotificationCategories,
  notificationCategories,
} from '#src/lib/notifications.ts';

import type {
  ConfigProvider_TeamsPlayingNextGameWeek,
  ConfigQueryVariables,
  PingConfigQuery,
  PingConfigQueryVariables,
  ReportTelemetry,
  ReportTelemetryVariables,
  ScoutableSo5Competitions_query,
  UpcomingSo5Fixture_query,
} from './__generated__/Provider.graphql.ts';
import type { AlgoliaCardIndexes, AlgoliaIndexes } from './index.ts';
import { ConfigContext } from './index.ts';
import type { ConfigQuery, ConfigQuery_currentUser } from './types.ts';

const teamPlayingNextGameWeekFragment = gqlMacro`
  fragment ConfigProvider_TeamsPlayingNextGameWeek on TeamsPlayingNextGameWeek {
    slug
    gameWeek
    shortDisplayName
    longDisplayName
    endDate
    startDate
    teamSlugs
  }
` as TypedDocumentNode<ConfigProvider_TeamsPlayingNextGameWeek>;

const scoutableSo5CompetitionsFragment = gql`
  fragment ScoutableSo5Competitions_query on Query {
    so5 {
      id
      currentManagerHasLiveGames(sport: $sport)
      cardMaxGrade(sport: $sport)
      inSeasonName: seasonalityName(seasonality: IN_SEASON, sport: $sport)
      allSeasonsName: seasonalityName(seasonality: ALL_SEASONS, sport: $sport)
      myAvailableFixtureStory {
        id
        aasmState
      }
      so5Competitions(sport: $sport, eventType: CLASSIC) {
        slug
        displayName
        logoUrl
        iconUrl
        backgroundUrl
        algoliaFilters
        scoutable
        competitions {
          slug
          displayName
          pictureUrl
        }
      }
    }
  }
` as TypedDocumentNode<ScoutableSo5Competitions_query>;

const upcomingSo5FixtureFragment = gql`
  fragment UpcomingSo5Fixture_query on Query {
    so5 {
      nextSo5Fixture: so5Fixture(type: UPCOMING, sport: $sport) {
        slug
        cutOffDate
      }
    }
  }
` as TypedDocumentNode<UpcomingSo5Fixture_query>;

const CONFIG_QUERY = gqlMacro`
  query ConfigQuery(
    $sport: Sport!
    $notificationCategories: [NotificationCategoryInput!]!
    $importantNotificationCategories: [NotificationCategoryInput!]!
    $withCollectionPredictions: Boolean = true
  ) {
    config {
      id
      algoliaIndexSuffix
      algoliaApplicationId
      algoliaSearchApiKey
      baseBankAddress
      ethereumBankDepositAddress
      baseCrossDomainDepositAddress
      l1MessengerAddress
      starkExchangeAddress
      ethereumGatewayMinterProxyAddress
      ethereumNetworkId
      ethereumEndpoint
      solanaBankAuthority
      sorareEncryptionKey
      minimumReceiveWeiAmount
      marketFeeRateBasisPoints
      idealDepositFeesAmountMinor
      payByBankDepositFeesAmountMinor
      mangopayAmexFeesBasisPoints: creditCardFeesBasisPoints(
        creditCardType: AMEX
        provider: MANGOPAY
      )
      mangopayVisaFeesBasisPoints: creditCardFeesBasisPoints(
        creditCardType: CB_VISA_MASTERCARD
        provider: MANGOPAY
      )
      paypalDepositFeesBasisPoints
      mangopayApplePayDepositFeesBasisPoints
      stripeCreditCardFeesBasisPoints: creditCardFeesBasisPoints(
        provider: STRIPE
      )
      paypalMinimumBidAmount: minimumBidAmount(provider: PAYPAL) {
        ...MonetaryAmountFragment_monetaryAmount
      }
      exchangeRate {
        id
        ethRates {
          ...RatesFragment_rates
        }
        time
      }
      defaultFiatCurrency
      ethQuantum
      so5 {
        id
        teamsPlayingNextDailyGameWeeks: teamsPlayingNextGameWeeks(
          eventType: DAILY
          sport: $sport
        ) {
          slug
          ...ConfigProvider_TeamsPlayingNextGameWeek
        }
        teamsPlayingNextClassicGameWeeks: teamsPlayingNextGameWeeks(
          eventType: CLASSIC
          sport: $sport
          first: 5
        ) {
          slug
          ...ConfigProvider_TeamsPlayingNextGameWeek
        }
        dailyTasksExpiresAt: tasksExpiresAt(periodicity: DAILY)
      }
      currentLocation {
        countryCode
        regionCode
      }
      counts {
        usersCount
      }
      activeRevealableCardCampaigns(sport: $sport) {
        slug
        name
        environmentPictureUrl
        blueprintCardEditions
        season {
          startYear
        }
      }
      referralProgramConfig(sport: $sport) {
        id
        maxDiscount {
          ...MonetaryAmountFragment_monetaryAmount
        }
      }
    }
    currentUser {
      slug
      ...CurrentUserProvider_currentUser
    }
    tokens {
      promotionalEvents: promotionalEvents(sport: $sport) {
        slug
        name
        title
        blogUrl
        description
        logoUrl
        pictureUrl
        filterPictureUrl
        secondaryBannerPictureUrl
        startDate
        endDate
        algoliaFilters
        ... on MarketPromotionalEvent {
          slug
          hasInstantBuy
        }
        ... on SpecialEditionPromotionalEvent {
          slug
          hasInstantBuy
        }
        ... on CardShardThresholdsPromotionalEvent {
          slug
          eligibilityRules {
            id
            eligibleRarities
          }
        }
      }
    }
    market {
      id
      myCommonDailyClaimTask(sport: $sport) {
        id
        aasmState
      }
      commonWheelTasks: myWheelTasks(sport: $sport, rarity: common) {
        id
        aasmState
      }
      limitedWheelTasks: myWheelTasks(sport: $sport, rarity: limited) {
        id
        aasmState
      }
      rareWheelTasks: myWheelTasks(sport: $sport, rarity: rare) {
        id
        aasmState
      }
      superRareWheelTasks: myWheelTasks(sport: $sport, rarity: super_rare) {
        id
        aasmState
      }
      uniqueWheelTasks: myWheelTasks(sport: $sport, rarity: unique) {
        id
        aasmState
      }
      commonWheelConfig: activeWheelConfig(rarity: common, sport: $sport) {
        slug
        ticketDistributionEndDate
      }
      limitedWheelConfig: activeWheelConfig(rarity: limited, sport: $sport) {
        slug
        ticketDistributionEndDate
      }
      rareWheelConfig: activeWheelConfig(rarity: rare, sport: $sport) {
        slug
        ticketDistributionEndDate
      }
      superRareWheelConfig: activeWheelConfig(rarity: super_rare, sport: $sport) {
        slug
        ticketDistributionEndDate
      }
      uniqueWheelConfig: activeWheelConfig(rarity: unique, sport: $sport) {
        slug
        ticketDistributionEndDate
      }
    }
    cardSet {
      id
      currentSet(sport: $sport) {
        slug
        name
        endDate
      }
      nextSet(sport: $sport) {
        slug
        name
        startDate
        endDate
      }
      maximumInviteTasksAsReferrer(sport: $sport)
      replayFinished(sport: $sport)
    }
    ...ScoutableSo5Competitions_query
    ...UpcomingSo5Fixture_query
  }
  ${currentUser}
  ${teamPlayingNextGameWeekFragment}
  ${scoutableSo5CompetitionsFragment}
  ${upcomingSo5FixtureFragment}
  ${ratesFragment}
  ${monetaryAmountFragment}
` as TypedDocumentNode<ConfigQuery, ConfigQueryVariables>;

const PING_QUERY = gqlMacro`
  query PingConfigQuery($sport: Sport!) {
    config {
      id
      exchangeRate {
        id
        ethRates { ...RatesFragment_rates }
        time
      }
      minimumReceiveWeiAmount
    }
    so5 {
      currentManagerHasLiveGames(sport: $sport)
    }
    currentUser {
      slug
    }
  }
  ${ratesFragment}
` as TypedDocumentNode<PingConfigQuery, PingConfigQueryVariables>;

const TM_MUTATION = gqlMacro`
  mutation ReportTelemetry($input: reportTelemetryInput!) {
    reportTelemetry(input: $input) {
      errors {
        message
      }
    }
  }
` as TypedDocumentNode<ReportTelemetry, ReportTelemetryVariables>;

type Props = {
  children: ReactNode;
};

const getRandomInt = (max: number) => {
  return Math.floor(Math.random() * max);
};

const pingInterval = 115_000 + getRandomInt(10_000); // 2Min +-5 sec;
const backgroundPingInterval = 1_795_000 + getRandomInt(10_000); //30 Min +- 5sec;
const tmInterval = 30_000 + getRandomInt(5_000);

export const loader = queryLoader(CONFIG_QUERY, ({ sport }) => ({
  sport,
  notificationCategories,
  importantNotificationCategories,
}));

/**
 * Provides environment dependent configuration keys.
 * Aggregates all startup loading states so that initialization can be done in parallel
 */
export const ConfigProvider = ({ children }: Props) => {
  const { data, refetch, loading } = useQueryLoader<ConfigQuery>();
  const { sport } = useSportContext();
  const [isPingQueryReady] = useTimeout(pingInterval);

  const isVisible = usePageVisibility();

  useQuery(PING_QUERY, {
    fetchPolicy: 'network-only',
    errorPolicy: 'ignore',
    pollInterval: isVisible ? pingInterval : backgroundPingInterval,
    skip: !isPingQueryReady(),
    variables: { sport },
  });
  const {
    flags: { useReportTelemetry },
  } = useFeatureFlags();

  const { refreshWsCable } = useGraphqlContext();
  const flushOperations = useTMContext()?.flushOperations;
  const [tm] = useMutation(TM_MUTATION, {
    fetchPolicy: 'network-only',
    errorPolicy: 'ignore',
  });

  const algoliaIndexSuffix = data?.config.algoliaIndexSuffix;

  const { algoliaCardIndexes, algoliaIndexes } = useMemo(() => {
    const aci: AlgoliaCardIndexes = {
      // blockchain cards indices
      New: `BlockchainCard_New${algoliaIndexSuffix}`,
      'Ending Soon': `BlockchainCard_EndingSoon${algoliaIndexSuffix}`,
      'Newly Listed': `BlockchainCard_NewlyListed${algoliaIndexSuffix}`,
      'Highest Average Score': `BlockchainCard_HighestAverageScore${algoliaIndexSuffix}`,
      'Highest Price': `BlockchainCard${algoliaIndexSuffix}`,
      'Lowest Price': `BlockchainCard_LowestPrice${algoliaIndexSuffix}`,
      'Best Value': `BlockchainCard_BestValue${algoliaIndexSuffix}`,
      'Popular Player': `BlockchainCard_Popularity${algoliaIndexSuffix}`,

      'Cards On Sale Ending Soon': `CardsOnSale_EndingSoon${algoliaIndexSuffix}`,
      'Cards On Sale Newly Listed': `CardsOnSale_NewlyListed${algoliaIndexSuffix}`,
      'Cards On Sale Highest Average Score': `CardsOnSale_HighestAverageScore${algoliaIndexSuffix}`,
      'Cards On Sale Highest Price': `CardsOnSale${algoliaIndexSuffix}`,
      'Cards On Sale Lowest Price': `CardsOnSale_LowestPrice${algoliaIndexSuffix}`,
      'Cards On Sale Best Value': `CardsOnSale_BestValue${algoliaIndexSuffix}`,
      'Cards On Sale Popular Player': `CardsOnSale_Popularity${algoliaIndexSuffix}`,
      'Cards On Sale Bids Count': `CardsOnSale_BidsCount${algoliaIndexSuffix}`,
    };

    const ai: AlgoliaIndexes = {
      ...aci,
      User: `User${algoliaIndexSuffix}`,
      Player: `Player${algoliaIndexSuffix}`,
      Club: `Club${algoliaIndexSuffix}`,
      League: `League${algoliaIndexSuffix}`,
      Country: `Country${algoliaIndexSuffix}`,
      'National Team': `NationalTeam${algoliaIndexSuffix}`,
      Competition: `Competition${algoliaIndexSuffix}`,
    };
    return {
      algoliaIndexes: ai,
      algoliaCardIndexes: aci,
    };
  }, [algoliaIndexSuffix]);

  const value = useMemo(() => {
    if (!data) return null;
    const { config, tokens, so5, market, cardSet } = data;
    const { exchangeRate, minimumReceiveWeiAmount } = config;

    return {
      ...config,
      ...tokens,
      ...market,
      cardSet,
      so5: {
        ...config.so5,
        ...so5,
      },
      wheel: {
        tasks: [
          ...market.commonWheelTasks,
          ...market.limitedWheelTasks,
          ...market.rareWheelTasks,
          ...market.superRareWheelTasks,
          ...market.uniqueWheelTasks,
        ],
        commonWheelTasks: market.commonWheelTasks,
        commonWheelConfig: market.commonWheelConfig,
        limitedWheelConfig: market.limitedWheelConfig,
        rareWheelConfig: market.rareWheelConfig,
        superRareWheelConfig: market.superRareWheelConfig,
        uniqueWheelConfig: market.uniqueWheelConfig,
      },
      marketFeeRateBasisPoints: config.marketFeeRateBasisPoints / 10000,
      marketFeePercentagePoints: BigInt(config.marketFeeRateBasisPoints) / 100n,
      minimumReceiveWeiAmount,
      algoliaIndexes,
      algoliaCardIndexes,
      currentUser: data.currentUser,
      exchangeRate: {
        ...exchangeRate,
        getEthRate: (to: FiatCurrency) =>
          getMinorRate(exchangeRate.ethRates, to).div(100).toNumber(),
      },
      refetch,
      loading,
      updateQuery: (newCurrentUser: ConfigQuery_currentUser) => {
        coreCache.updateQuery(
          {
            query: CONFIG_QUERY,
          },
          () => ({ ...data, currentUser: newCurrentUser })
        );
        refreshWsCable();
      },
      defaultFiatCurrency: config.defaultFiatCurrency,
    };
  }, [
    data,
    refetch,
    loading,
    refreshWsCable,
    algoliaCardIndexes,
    algoliaIndexes,
  ]);

  useInterval(
    () => {
      if (flushOperations) {
        const operations = flushOperations();
        if (!operations.length) {
          return;
        }
        tm({ variables: { input: { operations } } });
      }
    },
    useReportTelemetry ? tmInterval : null
  );

  const setSlug =
    value?.cardSet?.currentSet?.slug || value?.cardSet?.nextSet?.slug;

  useEffect(() => {
    if (setSlug) {
      document.body.setAttribute('data-theme-set-color', setSlug);
    }
  }, [setSlug]);

  if (!value) return <LoadingIndicator fullScreen />;

  return (
    <RedirectAfterSignIn currentUser={value.currentUser}>
      <ConfigContext value={value}>{children}</ConfigContext>
    </RedirectAfterSignIn>
  );
};
